import {
	SUBSCRIPTIONS_API_ROUTE,
	SUBSCRIPTIONS_GET_PROVIDERS,
} from 'apiRoutes';
import { Provider } from 'domain/subscription/interfaces/Subscription';
import { useGetOnMount } from 'hooks/useGetOnMount';
import { DefaultProviderBlacklistUnsavedChangesModal } from './DefaultProviderBlacklistUnsavedChangesModal';
import useSubmit, { POST } from 'hooks/useSubmit';
import { RequestStatus } from 'types/RequestStatus';

import {
	FunctionComponent,
	ReactNode,
	createContext,
	useContext,
	useEffect,
	useState,
} from 'react';
import { useBlockNavigationOnUnsavedChanges } from 'hooks/useBlockNavigationOnUnsavedChanges';

export type DefaultProviderBlacklistContextType = {
	allProviders?: Provider[];
	blacklistedProviders: Provider[];
	originalBlacklistedProviders: Provider[];
	addedProviders: Provider[];
	removedProviders: Provider[];
	isLoading: boolean;
	setBlacklistedProviders: React.Dispatch<React.SetStateAction<Provider[]>>;
	setOriginalBlacklistedProviders: React.Dispatch<
		React.SetStateAction<Provider[]>
	>;
	addProvider: (provider: Provider) => void;
	removeProvider: (provider: Provider) => void;
	resetChanges: () => void;
	submitChanges: () => Promise<void>;
};

export const DefaultProviderBlacklistContext =
	createContext<DefaultProviderBlacklistContextType | null>(null);

const DefaultProviderBlacklistContextProvider: FunctionComponent<{
	children: ReactNode;
}> = ({ children }) => {
	const [allProviders, setAllProviders] = useState<Provider[]>();
	const [blacklistedProviders, setBlacklistedProviders] = useState<
		Provider[]
	>([]);
	const [originalBlacklistedProviders, setOriginalBlacklistedProviders] =
		useState<Provider[]>([]);
	const [addedProviders, setAddedProviders] = useState<Provider[]>([]);
	const [removedProviders, setRemovedProviders] = useState<Provider[]>([]);

	const { data: providerOptions } = useGetOnMount<Provider[]>(
		`${SUBSCRIPTIONS_GET_PROVIDERS}?applicationId=${process.env.REACT_APP_MARKETS_APPLICATION_ID}`,
	);

	const [
		updateDefaultProviderBlacklist,
		updateDefaultProviderBlacklistStatus,
	] = useSubmit(
		`${SUBSCRIPTIONS_API_ROUTE}/UpdateDefaultProviderBlacklist`,
		POST,
		{
			messageOnSuccess: 'Default provider blacklist updated successfully',
			messageOnError: 'Error updating default provider blacklist',
			showApiError: true,
		},
	);

	const isLoading =
		updateDefaultProviderBlacklistStatus === RequestStatus.LOADING;

	useEffect(() => {
		if (!providerOptions) return;

		setAllProviders(providerOptions);
	}, [providerOptions]);

	const resetChanges = () => {
		setAddedProviders([]);
		setRemovedProviders([]);
		setBlacklistedProviders(originalBlacklistedProviders);
	};

	const addProvider = (provider: Provider) => {
		setAddedProviders((prev) => [...prev, provider]);
		setBlacklistedProviders((prev) => [...prev, provider]);
		setRemovedProviders((prev) => prev.filter((p) => p.id !== provider.id));
	};

	const removeProvider = (provider: Provider) => {
		setBlacklistedProviders((prev) =>
			prev.filter((p) => p.id !== provider.id),
		);

		const wasAdded = addedProviders.some((p) => p.id === provider.id);

		setAddedProviders((prev) => prev.filter((p) => p.id !== provider.id));

		if (!wasAdded) {
			setRemovedProviders((prev) => [...prev, provider]);
		}
	};

	const submitChanges = async () => {
		const response = await updateDefaultProviderBlacklist({
			providerCodes: blacklistedProviders.map((p) => p.code),
		});

		if (response.success) {
			setOriginalBlacklistedProviders(blacklistedProviders);
			setAddedProviders([]);
			setRemovedProviders([]);
		}
	};

	const isDirty = addedProviders.length > 0 || removedProviders.length > 0;

	const {
		isBlocking: showNavigationModal,
		handleDiscardChanges,
		handleKeepEditing,
		handleSaveAndProceed,
	} = useBlockNavigationOnUnsavedChanges({
		when: isDirty,
		onDiscard: resetChanges,
		onSave: submitChanges,
	});

	return (
		<DefaultProviderBlacklistContext.Provider
			value={{
				allProviders,
				blacklistedProviders,
				originalBlacklistedProviders,
				addedProviders,
				removedProviders,
				isLoading,
				setBlacklistedProviders,
				setOriginalBlacklistedProviders,
				addProvider,
				removeProvider,
				resetChanges,
				submitChanges,
			}}
		>
			{children}
			<DefaultProviderBlacklistUnsavedChangesModal
				isOpen={showNavigationModal}
				isLoading={isLoading}
				onDiscard={handleDiscardChanges}
				onKeepEditing={handleKeepEditing}
				onSave={handleSaveAndProceed}
				addedProviders={addedProviders}
				removedProviders={removedProviders}
			/>
		</DefaultProviderBlacklistContext.Provider>
	);
};

export function useDefaultProviderBlacklistContext() {
	const context = useContext(DefaultProviderBlacklistContext);

	if (!context) {
		throw new Error(
			'useDefaultProviderBlacklistContext must be used within the DefaultProviderBlacklistContextProvider',
		);
	}

	return context;
}

export default DefaultProviderBlacklistContextProvider;
